import * as React from "react";

import { isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { type CalendarDate, createDate, fromParts } from "../dateAdapter";
import { tick } from "../lib";
import { invoke, isNil } from "../lib/utils";
import type { BasePickerOnChangeData } from "../pickers/BasePicker";
import YearPicker from "../pickers/YearPicker";
import DayPicker from "../pickers/dayPicker/DayPicker";
import MonthPicker from "../pickers/monthPicker/MonthPicker";
import InputView from "../views/InputView";
import BaseInput, {
  type BaseInputProps,
  type BaseInputState,
  type DateRelatedProps,
  type DisableValuesProps,
  type EnableValuesProps,
  type MarkedValuesProps,
  type MinMaxValueProps,
  type MultimodeProps,
} from "./BaseInput";
import {
  buildValue,
  dateValueToString,
  parseArrayOrValue,
  parseValue,
} from "./parse";
import {
  clampMinMaxToValue,
  getDisabledMonths,
  getDisabledYears,
} from "./shared";

type CalendarMode = "year" | "month" | "day";

function getNextMode(currentMode: CalendarMode) {
  if (currentMode === "year") {
    return "month";
  }
  if (currentMode === "month") {
    return "day";
  }

  return "year";
}

function getPrevMode(currentMode: CalendarMode) {
  if (currentMode === "day") {
    return "month";
  }
  if (currentMode === "month") {
    return "year";
  }

  return "day";
}

export interface DateInputProps
  extends
    BaseInputProps,
    DateRelatedProps,
    MultimodeProps,
    DisableValuesProps,
    EnableValuesProps,
    MarkedValuesProps,
    MinMaxValueProps {
  /** Display mode to start. */
  startMode?: CalendarMode;
}

export type DateInputOnChangeData = DateInputProps;

interface DateInputState extends BaseInputState {
  mode: CalendarMode;
  year: number | undefined;
  month: number | undefined;
  date: number | undefined;
}

class DateInput extends BaseInput<DateInputProps, DateInputState> {
  /**
   * Component responsibility:
   *  - parse input value
   *  - handle underlying picker change
   */
  public static readonly defaultProps = {
    ...BaseInput.defaultProps,
    dateFormat: "dd-MM-yyyy",
    startMode: "day",
    preserveViewMode: true,
    icon: CalendarIcon,
  };

  constructor(props: DateInputProps) {
    super(props);
    const parsedValue = parseValue(
      props.value,
      props.dateFormat || "",
      props.localization,
    );
    this.state = {
      mode: props.startMode || "day",
      popupIsClosed: true,
      year: parsedValue ? parsedValue.year() : undefined,
      month: parsedValue ? parsedValue.month() : undefined,
      date: parsedValue ? parsedValue.date() : undefined,
    };
  }

  public componentDidUpdate = (prevProps: DateInputProps) => {
    // update internal date if ``value`` prop changed and successuffly parsed
    if (prevProps.value !== this.props.value) {
      const dateFormat = this.props.dateFormat || "dd-MM-yyyy";
      const parsed = parseValue(
        this.props.value,
        dateFormat,
        this.props.localization,
      );
      if (parsed) {
        this.setState({
          year: parsed.year(),
          month: parsed.month(),
          date: parsed.date(),
        });
      }
    }
  };

  public render() {
    const {
      value,
      dateFormat,
      initialDate,
      disable,
      enable,
      maxDate,
      minDate,
      preserveViewMode,
      startMode,
      closable,
      markColor,
      marked,
      localization,
      onChange,
      ...rest
    } = this.props;
    const safeDateFormat = dateFormat || "dd-MM-yyyy";

    return (
      <InputView
        closePopup={this.closePopup}
        openPopup={this.openPopup}
        popupIsClosed={this.state.popupIsClosed}
        onMount={this.onInputViewMount}
        onFocus={this.onFocus}
        onChange={this.onInputValueChange}
        {...(rest as any)}
        renderPicker={() => this.getPicker()}
        value={dateValueToString(value, safeDateFormat, localization)}
      />
    );
  }

  private parseInternalValue(): CalendarDate | undefined {
    /*
      Creates moment instance from values stored in component's state
      (year, month, date) in order to pass this moment instance to
      underlying picker.
      Return undefined if none of these state fields has value.
    */
    const { year, month, date } = this.state;
    if (!isNil(year) || !isNil(month) || !isNil(date)) {
      return fromParts({ year, month, date });
    }
  }

  private getPicker = () => {
    const {
      value,
      initialDate,
      dateFormat,
      disable,
      enable,
      inline,
      marked,
      markColor,
      localization,
      tabIndex,
      pickerWidth,
      pickerStyle,
    } = this.props;
    const { minDate, maxDate } = clampMinMaxToValue(
      value,
      this.props.minDate,
      this.props.maxDate,
    );
    const safeDateFormat = dateFormat || "dd-MM-yyyy";
    const pickerProps = {
      isPickerInFocus: this.isPickerInFocus,
      isTriggerInFocus: this.isTriggerInFocus,
      inline,
      onCalendarViewMount: this.onCalendarViewMount,
      closePopup: this.closePopup,
      tabIndex,
      pickerWidth,
      pickerStyle,
      onChange: this.handleSelect,
      onHeaderClick: this.switchToPrevMode,
      initializeWith: buildValue(
        this.parseInternalValue(),
        initialDate,
        localization,
        safeDateFormat,
      ),
      value: buildValue(value, null, localization, safeDateFormat, null),
      enable: parseArrayOrValue(enable, safeDateFormat, localization),
      minDate: parseValue(minDate, safeDateFormat, localization),
      maxDate: parseValue(maxDate, safeDateFormat, localization),
      localization,
    };
    const disableParsed = parseArrayOrValue(
      disable,
      safeDateFormat,
      localization,
    );
    const markedParsed = parseArrayOrValue(
      marked,
      safeDateFormat,
      localization,
    );
    const { mode } = this.state;
    if (mode === "year") {
      return (
        <YearPicker
          {...pickerProps}
          disable={getDisabledYears(disableParsed!)}
        />
      );
    }
    if (mode === "month") {
      return (
        <MonthPicker
          {...pickerProps}
          hasHeader
          disable={getDisabledMonths(disableParsed!)}
        />
      );
    }

    return (
      <DayPicker
        {...pickerProps}
        disable={disableParsed}
        marked={markedParsed}
        markColor={markColor}
      />
    );
  };

  private switchToNextModeUndelayed = (): void => {
    this.setState(({ mode }) => {
      return { mode: getNextMode(mode) };
    }, this.onModeSwitch);
  };

  private switchToNextMode = (): void => {
    tick(this.switchToNextModeUndelayed);
  };

  private switchToPrevModeUndelayed = (): void => {
    this.setState(({ mode }) => {
      return { mode: getPrevMode(mode) };
    }, this.onModeSwitch);
  };

  private switchToPrevMode = (): void => {
    tick(this.switchToPrevModeUndelayed);
  };

  private onFocus = (): void => {
    if (!this.props.preserveViewMode) {
      this.setState({ mode: this.props.startMode! });
    }
  };

  private handleSelect = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: BasePickerOnChangeData,
  ) => {
    if (this.state.mode === "day" && this.props.closable) {
      this.closePopup();
    }
    this.setState(
      (prevState) => {
        const { mode } = prevState;
        if (mode === "day") {
          const outValue = fromParts(value).format(this.props.dateFormat!);
          invoke(this.props, "onChange", e, { ...this.props, value: outValue });
        }

        return { year: value.year, month: value.month, date: value.date };
      },
      () => this.state.mode !== "day" && this.switchToNextMode(),
    );
  };

  /** Keeps internal state in sync with input field value. */
  private onInputValueChange = (
    e: React.SyntheticEvent<HTMLElement>,
    { value }: { value: string },
  ) => {
    const parsedValue = createDate(value, this.props.dateFormat);
    if (parsedValue && isValid(parsedValue.toDate())) {
      this.setState({
        year: parsedValue.year(),
        month: parsedValue.month(),
        date: parsedValue.date(),
      });
    }
    invoke(this.props, "onChange", e, { ...this.props, value });
  };
}

export default DateInput;
